import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { OrganizationsNavigationProvider } from '../../../organizations/providers/navigation-provider';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { CoachesFormComponent } from '../../components/form/form';
import { CoachesProvider, CoachRow } from '../../providers/coaches-provider';
import { BreadcrumbProvider } from '../../../breadcrumb/providers/breadcrumb-provider';
import { CoachesAthletesFormComponent } from '../../components/form/athletes';
import { CoachesTeamsFormComponent } from '../../components/form/teams';
import { CoachRights } from '../../providers/coaches-rights';
import { UsersProvider } from '../../../users/providers/users-provider';
import { forkJoin, Observable, of, Subscription, switchMap, takeUntil, throwError } from 'rxjs';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ModuleMenu } from "../../../common/models/menu";
import { Organization } from "../../../common/models/organization";
import { Athlete } from "../../../common/models/athlete";
import { Team } from "../../../common/models/team";
import { SimpleMatDataSource } from "../../../common/providers/simple-mat-data-source";
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { AthleteRow, AthletesProvider } from "../../../athletes/providers/athletes-provider";
import { TeamsProvider } from "../../../teams/providers/teams-provider";
import { InfoDialogComponent } from "../../../common/components/info-dialog/info-dialog";
import { ConfirmDialogComponent } from "../../../common/components/confirm-dialog/confirm-dialog";
import { catchError, map } from "rxjs/operators";
import { User } from "../../../common/models/user";
import { Coach } from "../../../common/models/coach";
import { MatIcon } from "@angular/material/icon";
import { NgIf, NgOptimizedImage } from "@angular/common";
import {
  MatCell,
  MatCellDef,
  MatColumnDef, MatHeaderCell,
  MatHeaderCellDef, MatHeaderRow,
  MatHeaderRowDef, MatRow,
  MatRowDef,
  MatTable
} from "@angular/material/table";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { MatFormField, MatSuffix } from "@angular/material/form-field";
import { MatIconButton } from "@angular/material/button";
import { MatInput } from "@angular/material/input";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { MatTooltip } from "@angular/material/tooltip";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { OrganizationRole } from "../../../common/models/organization-role";
import { Paginator } from "../../../common/models/paginator";
import { ApiPaginator } from "../../../common/components/api-paginator/api-paginator";
import { CenterLoaderComponent } from "../../../common/components/center-loader/center-loader";

@Component({
  selector: 'coaches-list-page',
  providers: [],
  styleUrls: ['./list.scss'],
  standalone: true,
  imports: [
    MatPaginator,
    MatIcon,
    TranslateModule,
    MatSortModule,
    NgIf,
    MatHeaderRowDef,
    MatCellDef,
    MatRowDef,
    MatHeaderCellDef,
    ModuleContentComponent,
    MatFormField,
    MatColumnDef,
    MatSuffix,
    MatIconButton,
    MatInput,
    MatMenuItem,
    MatTable,
    MatMenu,
    MatTooltip,
    MatMenuTrigger,
    MatCell,
    MatHeaderRow,
    MatHeaderCell,
    MatRow,
    Error404Component,
    Error401Component,
    PageContentComponent,
    HeaderComponent,
    NgOptimizedImage,
    ApiPaginator,
    CenterLoaderComponent
  ],
  templateUrl: './list.html'
})
export class CoachesListPageComponent implements AfterViewInit, OnInit, OnDestroy {
    public DESCRIPTION_MAX_LENGTH = 5000;

    public pageTitle = 'Coaches';
    public mainActions: ModuleMenu[] = [];
    public isLoading = true;
    public is401 = false;
    public is404 = false;

    public activePage: number = 1;
    public activeOrderBy: string = 'name_asc';
    private userFilter = '';

    public organization: Organization = {} as Organization;
    public availableAthletes: Athlete[] = [];
    public availableTeams: Team[] = [];
    public coachesPaginator: Paginator = {} as Paginator;

    public rights: CoachRights = new CoachRights(this._utils);
    public dataSource: SimpleMatDataSource = new SimpleMatDataSource([]);
    public columns: string[] = ['avatar', 'name', 'athletes', 'teams', 'actions'];
    public superCoach = false;

    public formTitle: string = '';
    private crumb: string |null = null;
    private detailUrl: string |null = null;
    private subscriptions: Subscription[] = [];

    @ViewChild(MatPaginator, {static: false}) public paginator: MatPaginator | undefined;
    @ViewChild(MatSort, {static: false}) public sorter: MatSort  | undefined;

    constructor(
        public conf: ConfigProvider,
        public breadcrumbProvider: BreadcrumbProvider,
        public currentUser: CurrentUser,
        private _utils: UtilsProvider,
        private _title: Title,
        private _organizationNavigation: OrganizationsNavigationProvider,
        private _router: Router,
        private _dialoger: MatDialog,
        private _toaster: MatSnackBar,
        private _currentUserRoles: CurrentUserRolesProvider,
        private _route: ActivatedRoute,
        private _translater: TranslateService,
        private _dialog: MatDialog,
        private _athletesProvider: AthletesProvider,
        private _teamsProvider: TeamsProvider ,
        private _coachesProvider: CoachesProvider,
        private _usersProvider: UsersProvider
    ) {
    }

    ngOnInit(): void {
        this.superCoach = this._route.snapshot.data['isSuperCoaches'];
        this.formTitle = this._route.snapshot.data['title'];
        this.crumb = this._route.snapshot.data['crumb'];
        this.detailUrl = this._route.snapshot.data['detailUrl'];
    }

    public ngAfterViewInit() {
      this._route.params.pipe(
        takeUntil(this.subscriptions),
        switchMap(() => this.loadOrganization()), // Assuming loadOrganization returns an Observable
        catchError((error) => {
          this.onError(error);
          return of(null); // Handle the error and continue the stream with a null value
        })
      ).subscribe({
        next: () => {
          this.initPage();
        },
        error: (err) => this.onError(err) // Additional error handling if needed
      });
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public onDisplayCoachClicked(toView: CoachRow) {
        this._router.navigateByUrl('/' + this.organization?.code + this.detailUrl + toView.user.id);
    }

    public onAddCoachClicked(): void {
        const dialogRef: any = this._dialog.open(CoachesFormComponent, {
            data: {
                organization: this.organization,
                availableAthletes: this.availableAthletes,
                availableTeams: this.availableTeams,
                formTitle: 'Coach_creation',
                itsASuperCoach: this.superCoach
            }
        });

        // màj de la liste
        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((row: CoachRow) => {
                if (row) {
                  this.setCoachesRows({}).subscribe();
                }
            })
        );
    }

    public onModifyCoachClicked(toModify: CoachRow): void {
        const dialogRef: any = this._dialog.open(CoachesFormComponent, {
            data: {
                organization: this.organization,
                availableAthletes: this.availableAthletes,
                availableTeams: this.availableTeams,
                coachRow: toModify,
                formTitle: 'Coach_edition',
                itsASuperCoach: this.superCoach
            }
        });

        // màj de la liste
        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((row: CoachRow) => {
                if (row) {
                    for (let i = 0; i < this.dataSource.count; i++) {
                        if (this.dataSource.get(i).coach.id === row.coach.id) {
                            /**
                             * Enlève le coach de la liste si c'est un super-coach et qu'on se trouve sur
                             * la liste des coachs normaux, et inversément.
                             */
                            if (this.superCoach){
                                if (!row.isSuperCoach){
                                    this.dataSource.remove(i);
                                    return;
                                }
                            }else{
                                if (row.isSuperCoach){
                                    this.dataSource.remove(i);
                                    return;
                                }
                            }
                            // mets à jour les infos de la liste
                            this.dataSource.set(i, row);
                            return;
                        }
                    }
                }
            })
        );
    }

    public onManageAthletesClicked(toModify: CoachRow): void {

        const dialogRef: any = this._dialog.open(CoachesAthletesFormComponent, {
            data: {
                organization: this.organization,
                availableAthletes: this.availableAthletes,
                coachRow: toModify,
                formTitle: 'Coach_Athlete_edition',
                confirmButtonTitle: this._translater.instant('Save')
            }
        });

        // màj de la liste
        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((row: CoachRow) => {
                if (row) {
                    for (let i = 0; i < this.dataSource.count; i++) {
                        if (this.dataSource.get(i).coach.id === row.coach.id) {
                            this.dataSource.set(i, row);
                            return;
                        }
                    }
                }
            })
        );
    }

    public onManageTeamsClicked(toModify: CoachRow): void {

        const dialogRef: any = this._dialog.open(CoachesTeamsFormComponent, {
            data: {
                organization: this.organization,
                availableTeams: this.availableTeams,
                coachRow: toModify,
                formTitle: 'Coach_Team_edition',
                confirmButtonTitle: this._translater.instant('Save')
            }
        });

        // màj de la liste
        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((row: CoachRow) => {
                if (row) {
                    for (let i = 0; i < this.dataSource.count; i++) {
                        if (this.dataSource.get(i).coach.id === row.coach.id) {
                            this.dataSource.set(i, row);
                            return;
                        }
                    }
                }
            })
        );
    }

    public onEnableCoachClicked(toEnable: CoachRow, active: boolean): void {
      this.isLoading = true;
      this.enableCoach(toEnable.coach, active).subscribe({
        next: () => {
          for (let i = 0; i < this.dataSource.count; i++) {
              if (this.dataSource.get(i).coach.id === toEnable.coach.id) {
                  toEnable.user.active = +active;
                  this.dataSource.set(i, toEnable);
                  break;
              }
          }
          this.isLoading = false;
          this._toaster.open(this._translater.instant(active ? 'User_enabled' : 'User_disabled'), '', {duration: 5000});
        },
        error: (error) => {
          this.onError(error);
        },
      });
    }

    public onDeleteCoachClicked(toDelete: CoachRow): void {
        if (toDelete.athletes.length > 0 || toDelete.teams.length > 0){

            const dialog: MatDialogRef<any> = this._dialoger.open(InfoDialogComponent, {
                data: {
                    text: this._translater.instant('Coach_relations_no_delete')
                }
            });
        } else {

            const dialog: MatDialogRef<any> = this._dialoger.open(ConfirmDialogComponent, {
                data: {
                    confirmText: this._translater.instant('Confirm_delete_coach')
                }
            });

            this.subscriptions.push(
                dialog.afterClosed().subscribe((confirmed: boolean) => {
                    if (confirmed) {
                        this.isLoading = true;
                        this.deleteCoach(toDelete.coach).subscribe({
                            next: () => {
                                this.isLoading = false;
                                this._toaster.open(this._translater.instant('Coach_deleted'), '', {duration: 5000});
                            },
                            error: () => {
                                this.isLoading = false;
                                this._toaster.open(this._translater.instant('Coach_delete_error'), '', {duration: 5000});
                            }
                        });
                    }
                })
            );
        }
    }

    public onSortChange(event: any): void {
      if (event.active === 'name') {
        this.setCoachesRows({orderBy: `name_${event.direction}`}).subscribe();
      }
        this.dataSource.sortBy(event.active, event.direction);
    }

    public onFilterKeyup(filter: string): void {
      // a new search always restarts on the first page (#476)
      this.setCoachesRows({coachName: filter, page: 1}).subscribe({
        error: (error) => this.onError(error),
      });
    }

    private loadOrganization(): Observable<void> {
       return this._organizationNavigation.getOrganisationFromRoute().pipe(
          map((organization: Organization|null) => {
              if (organization) {
                  this.organization = organization;
              } else {
                throw {error: 'Organization not found', status: 404};
              }
          })
      );
    }

    private initPage(): void {
        this.loadData().subscribe({
            next: () => {
              this.initNav();
              this.initSorters();
              this.initSearch();
              this.dataSource.sortBy('name', 'asc');
              this.isLoading = false;
            },
            error: (error) => this.onError(error)
        });
    }

    private initNav(): void {

        if (!this.rights.LIST) {
            this.is401 = true;
            return;
        }

        if (this.rights.SEND_INVITATION) {
            this.columns = ['avatar', 'name', 'athletes', 'teams', 'notvalidate', 'actions'];
        }

        this.dataSource.paginator = this.paginator!;

        if (this.rights.ADD) {
            this.mainActions = [
                {title: 'Add', icon: 'add', url: null, onClick: () => this.onAddCoachClicked()}
            ];
        }

        this.createPageTitle();
        this.createBreadcrumb();
    }

    private initSorters(): void {
        this.dataSource.addSortDefinition(
            'name',
            (a: AthleteRow, b: AthleteRow) => this._utils.sortRow((`${a.user.firstName} ${a.user.lastName}`).toLowerCase(), (`${b.user.firstName} ${b.user.lastName}`).toLowerCase()),
            (a: AthleteRow, b: AthleteRow) => this._utils.sortRow((`${b.user.firstName} ${b.user.lastName}`).toLowerCase(), (`${a.user.firstName} ${a.user.lastName}`).toLowerCase())
        );
    }

    private initSearch(): void {
        this.dataSource.sort = this.sorter!;
        this.dataSource.filterPredicate = (data: AthleteRow, filter: string) => {
            const str: string = this._utils.normalizeString(`${data.user.firstName} ${data.user.lastName}`);
            return str.includes(filter);
        };
    }

    private loadData(): Observable<any> {
      if (!this.organization) {
        return throwError(() => new Error('Organization not found'));
      }
      return forkJoin([
        this.setCoachesRows({}),
        this._athletesProvider.getAthletes(this.organization.id!),
        this._teamsProvider.getTeams({organizationId: this.organization.id!})
      ]).pipe(
      map(([coaches, athletes, teams] : [void, User[], Team[]]) => {
          this.availableAthletes = athletes;
          this.availableTeams = teams;

          this.rights.evaluateScreenRights({
            rolesIds: this._currentUserRoles.getRolesIds(this.organization?.id!)
          });

          return null; // You might want to adjust what you return based on your needs
        }),
        catchError((error) => {
          // Handle errors appropriately
          console.error('Error loading data', error);
          return throwError(() => error); // Rethrow or handle as needed
        })
      );
    }

    public pageChange(page: number): void {
      this.isLoading = true;
      this.setCoachesRows({page: page}).subscribe({
        next: () => {
          this.isLoading = false;
        },
        error: (error) => {
          this.onError(error);
        },
      });
    }

    private setCoachesRows({orderBy = this.activeOrderBy, page = this.activePage, coachName = this.userFilter}: {orderBy?: string, page?: number, coachName?: string}): Observable<void> {
        return this._coachesProvider.getCoachesPaginator({
          organizationId: this.organization?.id!,
          orderBy: orderBy,
          pageNumber: page,
          userName: coachName,
          role: this.superCoach ? OrganizationRole.ID_SUPERCOACH : OrganizationRole.ID_COACH
        }).pipe(
            map((coachesPaginator: Paginator) => {
              // Build the rows before publishing any state: a row that fails to build must not
              // leave the paginator showing a count for rows the table never receives (#476).
              const coaches: CoachRow[] = coachesPaginator.data.map((user: User) =>
                this._coachesProvider.createCoachRow(this.organization?.id!, user));

              this.activePage = page;
              this.activeOrderBy = this.activeOrderBy;
              this.userFilter = coachName;
              coachesPaginator.data = coaches;
              this.coachesPaginator = coachesPaginator;
              this.dataSource.replaceAllWith(coaches);

              this.dataSource.paginator = this.paginator!;
            })
        );
    }

    public onResendInvitationClicked(user: User) {
        this.isLoading = true;
        this._usersProvider.sendInvitation(user).subscribe({
            next: () => {
                this.isLoading = false;
                this._toaster.open(this._translater.instant('Invitation_sent'), '', { duration: 5000 });
            },
            error: () => {
                this.isLoading = false;
                this._toaster.open(this._translater.instant('Invitation_send_error'), '', { duration: 5000 });
            }
        });
    }

    private enableCoach(coach: Coach, active: boolean): Observable<any> {
      if (!coach.userId) {
        this.onError({error: 'User ID not found', status: 404});
        return of(null);
      }
      return this._usersProvider.updateUser(coach.userId, {'active': active});
    }

    private deleteCoach(coach: Coach): Observable<any> {
      if (!coach.userId) {
        this.onError({error: 'User ID not found', status: 404});
        return of();
      }
      return this._coachesProvider.deleteCoach(this.organization?.id!, coach.userId).pipe(
          map(() => {
              for (let i = 0; i < this.dataSource.count; i++) {
                  if (this.dataSource.get(i).coach.id === coach.id) {
                      this.dataSource.remove(i);
                      i = this.dataSource.count;
                  }
              }
          }),
      );
    }

    private createPageTitle(){
        this.pageTitle = this._translater.instant(this.formTitle!);
        this._title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
    }

    private createBreadcrumb() {
        this.breadcrumbProvider.setDefaultCrumbs([{
            shortTitle: this.crumb!,
            title: this.crumb!,
            url: this._router.url
        }]);
    }

    private onError(error: any): void {
        this.isLoading = false;

        if (error.status === 404) {
            this.is404 = true;
        } else if (error.status === 401) {
            this.is401 = true;
        }
    }
}
